import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, BedDouble, MapPin, Users } from "lucide-react";
import { useLanguage } from "../i18n";
import { useSEO } from "../seo";
import { API, imgUrl, propertyUrl, trackEvent } from "../data";

const EASE = [0.22, 1, 0.36, 1];

const COPY = {
  it: {
    title: "Le strutture | OGStay — Case vacanza in Ogliastra",
    desc: "Tutte le case e le ville selezionate da OGStay in Ogliastra: verifica disponibilità e prezzi in tempo reale sul portale di prenotazione ufficiale.",
    kicker: "Le strutture",
    h1: "Ogni casa,",
    h1i: "selezionata di persona.",
    sub: "Poche proprietà, seguite bene. Scegli la tua e verifica disponibilità e prezzi in tempo reale: la prenotazione avviene sul portale ufficiale OGStay.",
    guests: "ospiti",
    rooms: "camere",
    from: "da",
    night: "/ notte",
    cta: "Verifica disponibilità",
    note: "Disponibilità e prezzi sono sempre aggiornati sul portale di prenotazione ufficiale.",
  },
  en: {
    title: "Our properties | OGStay — Holiday homes in Ogliastra",
    desc: "All the homes and villas selected by OGStay in Ogliastra: check real-time availability and prices on the official booking portal.",
    kicker: "Our properties",
    h1: "Every home,",
    h1i: "personally selected.",
    sub: "Few properties, managed well. Pick yours and check real-time availability and prices: booking happens on the official OGStay portal.",
    guests: "guests",
    rooms: "bedrooms",
    from: "from",
    night: "/ night",
    cta: "Check availability",
    note: "Availability and prices are always up to date on the official booking portal.",
  },
};

export default function StrutturePage() {
  const { lang } = useLanguage();
  const c = COPY[lang];
  const [items, setItems] = useState([]);
  useSEO(c.title, c.desc);

  useEffect(() => {
    axios
      .get(`${API}/properties`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="properties-page" className="bg-warm pb-28 pt-36 sm:pt-44">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <p className="kicker text-navy" data-testid="properties-page-kicker">
          <span className="h-px w-10 bg-sky" />
          {c.kicker}
        </p>
        <h1 data-testid="properties-page-title" className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl">
          {c.h1}
          <span className="block italic text-med">{c.h1i}</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-mute sm:text-lg">{c.sub}</p>

        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-12">
          {items.map((prop, i) => {
            const id = prop.id || prop._id || prop.name;
            return (
              <motion.a
                key={id}
                href={propertyUrl(prop)}
                data-testid={`struttura-card-${i}`}
                data-property-id={id}
                onClick={() => trackEvent("property_booking_click", { property_id: String(id), property_name: prop.name, source: "properties" })}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, delay: (i % 3) * 0.12, ease: EASE }}
                className="group block"
              >
                <div className="overflow-hidden rounded-t-[10rem] rounded-b-[2rem] ring-1 ring-navy/10 transition-shadow duration-500 group-hover:shadow-[0_30px_60px_-20px_rgba(15,43,58,0.35)]">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={imgUrl(prop.images && prop.images.length ? prop.images[0] : null)}
                      alt={prop.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.07]"
                    />
                  </div>
                </div>
                <div className="mt-6 flex items-start justify-between gap-4 px-1">
                  <div>
                    <h2 className="font-display text-2xl font-semibold text-navy">{prop.name}</h2>
                    <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-mute">
                      <MapPin className="h-3.5 w-3.5 text-sky" />
                      {prop.location}
                    </p>
                    <p className="mt-2 flex items-center gap-4 text-xs font-bold uppercase tracking-wider text-mute">
                      <span className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        {prop.guests} {c.guests}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <BedDouble className="h-3.5 w-3.5" />
                        {prop.rooms} {c.rooms}
                      </span>
                    </p>
                  </div>
                  {prop.price && (
                    <div className="shrink-0 rounded-full bg-navy px-4 py-2 text-right">
                      <span className="block text-[10px] font-bold uppercase tracking-widest text-sky">{c.from}</span>
                      <span className="text-sm font-extrabold text-white">
                        {prop.price} <span className="text-[10px] font-semibold text-white/60">{c.night}</span>
                      </span>
                    </div>
                  )}
                </div>
                <span className="mt-4 inline-flex items-center gap-2 px-1 text-xs font-extrabold uppercase tracking-[0.16em] text-navy transition-colors group-hover:text-sky">
                  {c.cta}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </motion.a>
            );
          })}
        </div>

        <p className="mt-14 text-center text-xs font-semibold uppercase tracking-[0.18em] text-mute" data-testid="properties-page-note">
          {c.note}
        </p>
      </div>
    </div>
  );
}
